import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, ArrowUpRight, ExternalLink, GraduationCap, Briefcase, ChevronRight } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { PageMeta } from "@/components/site/PageMeta";
import { getSection } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function CVPage() {
    const [cv, setCv] = useState(null);
    const [home, setHome] = useState(null);

    useEffect(() => {
        getSection("cv").then((r) => setCv(r.data || {}));
        getSection("home").then((r) => setHome(r.data || {}));
    }, []);

    const c = cv || {};
    const quals = c.qualifications_flow || [];
    const work = c.work_experience || [];
    const qualBadges = home?.qualifications || [];

    return (
        <>
            <PageMeta title="CV — Aditya Baheti" description="Qualifications, work experience, and downloadable CV." path="/cv" />

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-6">
                <SectionLabel label="Curriculum Vitæ" />
                <Reveal>
                    <h1 className="font-display text-4xl sm:text-6xl leading-[1.05] tracking-tight max-w-3xl">A concise journey.</h1>
                </Reveal>
                <Reveal delay={0.05}>
                    <p className="mt-4 max-w-[72ch] text-base sm:text-lg text-muted-foreground leading-relaxed">{c.summary}</p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-7">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Qualifications</p>
                        <div className="flex flex-wrap gap-2" data-testid="cv-qualifications-block">
                            {qualBadges.map((q) => (
                                <Badge key={q} variant="outline" className="rounded-full border-border/70 bg-card font-mono text-[12px] tracking-wide px-3 py-1.5">{q}</Badge>
                            ))}
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {c.download_url ? (
                            <a
                                href={c.download_url}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="btn-outline-pill"
                                data-testid="cv-download-button"
                            >
                                <Download className="h-4 w-4" /> Download PDF <ArrowUpRight className="h-4 w-4" />
                            </a>
                        ) : (
                            <button
                                type="button"
                                disabled
                                className="btn-outline-pill opacity-60 cursor-not-allowed"
                                data-testid="cv-download-button"
                            >
                                <Download className="h-4 w-4" /> Download PDF (coming soon)
                            </button>
                        )}
                        <a
                            href="https://papers.ssrn.com/sol3/cf_dev/AbsByAuth.cfm?per_id=7711291"
                            target="_blank"
                            rel="noreferrer noopener"
                            className="btn-outline-pill accent"
                        >
                            SSRN <ExternalLink className="h-4 w-4" />
                        </a>
                    </div>
                </Reveal>
            </section>

            {/* Qualifications flowchart */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <SectionLabel label="Qualifications flow" />
                <Reveal>
                    <p className="text-muted-foreground max-w-[62ch] mt-2">Each step: qualification → university → year.</p>
                </Reveal>
                <Stagger className="mt-8 grid md:grid-cols-5 gap-3" data-testid="cv-qualifications-flow">
                    {quals.map((q, i) => (
                        <motion.div key={i} variants={staggerItem} className="relative">
                            <Card className="h-full p-5 rounded-2xl border-border/70 bg-card">
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-full bg-forest/10 grid place-items-center">
                                        <GraduationCap className="h-4 w-4 text-forest" />
                                    </div>
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-forest">Step {i + 1}</span>
                                </div>
                                <p className="mt-3 font-display text-lg leading-snug">{q.title}</p>
                                {q.institution && <p className="mt-1 text-xs text-muted-foreground">{q.institution}</p>}
                                {q.year && <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-brass">{q.year}</p>}
                            </Card>
                            {i < quals.length - 1 && (
                                <ChevronRight className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-forest/50" />
                            )}
                        </motion.div>
                    ))}
                </Stagger>
            </section>

            {/* Work experience timeline */}
            <section className="bg-secondary/40 border-y border-border/70">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                    <SectionLabel label="Work experience" />
                    <Reveal>
                        <p className="text-muted-foreground max-w-[62ch] mt-2">Roles across academia and leading Indian law firms.</p>
                    </Reveal>
                    <ol className="mt-8 relative border-l-2 border-border/70 pl-6 space-y-8" data-testid="cv-work-experience">
                        {work.map((w, i) => (
                            <Reveal key={i} delay={i * 0.05} as="li">
                                <div className="absolute -left-[11px] mt-2 h-5 w-5 rounded-full bg-background border-2 border-forest grid place-items-center">
                                    <Briefcase className="h-2.5 w-2.5 text-forest" />
                                </div>
                                {w.period && <p className="font-mono text-[11px] uppercase tracking-widest text-brass">{w.period}</p>}
                                <p className="mt-1 font-display text-2xl leading-snug">{w.title}</p>
                                {w.org && <p className="mt-1 text-sm text-muted-foreground">{w.org}</p>}
                                {w.body && <p className="mt-3 text-base text-muted-foreground max-w-[68ch] leading-relaxed">{w.body}</p>}
                            </Reveal>
                        ))}
                    </ol>
                </div>
            </section>
        </>
    );
}
